import React from 'react';
import { TeamAIMemberData, TeamAIDataService, RollingPeriodInfo } from '../services/teamAIDataService';
import './TeamAISection.css';
import ExportButtons from './ExportButtons';
import { exportTeamAIToCSV, exportTeamAIToPDF, TeamAIExportOptions } from '../services/teamAIExportService';
import TeamAIPeriodToggle, { TeamAIPeriodMode } from './TeamAIPeriodToggle';

interface TeamAISectionProps {
  teamAIData: TeamAIMemberData[];
  loading: boolean;
  error: string | null;
  periodInfo: RollingPeriodInfo;
  onSortChange: (metric: string) => void;
  sortBy: string;
  exportOptions?: TeamAIExportOptions;
  periodMode: TeamAIPeriodMode;
  onPeriodModeChange: (mode: TeamAIPeriodMode) => void;
}

const TeamAISection: React.FC<TeamAISectionProps> = ({
  teamAIData,
  loading,
  error,
  periodInfo,
  onSortChange,
  sortBy,
  exportOptions,
  periodMode,
  onPeriodModeChange
}) => {
  
  if (loading) {
    return (
      <div className="team-ai-section">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Team AI Leaderboard...</p>
          <small>Analyzing 4-week AI effectiveness across team members</small>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="team-ai-section">
        <div className="error-container">
          <h3>⚠️ Unable to Load Team AI Data</h3>
          <p>{error}</p>
          <small>Team AI Leaderboard requires team access (Central PS or My Team mode)</small>
        </div>
      </div>
    );
  }
  
  const formatDateRange = (startDate: string, endDate: string): string => {
    const start = new Date(startDate).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const end = new Date(endDate).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };
  
  const handleSort = (metric: string) => {
    onSortChange(metric);
  };
  
  const getSortIcon = (metric: string): string => {
    return sortBy === metric ? '▼' : '▽';
  };

  /**
   * Handle CSV export
   */
  const handleExportCSV = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Team AI CSV export');
      return;
    }
    exportTeamAIToCSV(teamAIData, exportOptions);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = () => {
    if (!exportOptions) {
      console.warn('Export options not provided for Team AI PDF export');
      return;
    }
    exportTeamAIToPDF(teamAIData, exportOptions);
  };
  
  const renderEffectivenessScore = (score: number) => {
    const level = TeamAIDataService.getAIEffectivenessLevel(score);
    return (
      <div className="effectiveness-score">
        <div 
          className="score-badge"
          style={{ backgroundColor: level.color }}
        >
          {score.toFixed(1)}
        </div>
        <div className="score-label" style={{ color: level.color }}>
          {level.label}
        </div>
      </div>
    );
  };
  
  const renderMetricCell = (value: number, suffix: string = '', decimals: number = 1) => {
    return (
      <span className="metric-value">
        {value.toFixed(decimals)}{suffix}
      </span>
    );
  };
  
  const renderTimeCell = (minutes: number) => {
    return (
      <span className="time-value">
        {TeamAIDataService.formatTime(minutes)}
      </span>
    );
  };
  
  return (
    <div className="ai-overview-table-container">
      {/* Header matching AI Overview and Non-AI Apps */}
      <div className="ai-overview-header">
        <div>
          <h3>🏆 Team AI Effectiveness Leaderboard</h3>
          <p>Rolling 4-week AI effectiveness analysis combining Cursor and AI tool usage</p>
        </div>
        
        {exportOptions && teamAIData.length > 0 && (
          <ExportButtons
            onExportCSV={handleExportCSV}
            onExportPDF={handleExportPDF}
            disabled={teamAIData.length === 0}
            className="team-ai-export-container"
          />
        )}
      </div>

      {/* Period Mode Toggle */}
      <div className="ai-overview-stats" style={{ marginBottom: '1rem' }}>
        <div className="stats-section">
          <TeamAIPeriodToggle
            currentMode={periodMode}
            onModeChange={onPeriodModeChange}
            disabled={loading}
          />
        </div>
      </div>

      {/* Stats section matching other screens */}
      <div className="ai-overview-stats">
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">Team Members:</span>
            <span className="stat-value">{teamAIData.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Period:</span>
            <span className="stat-value">{formatDateRange(periodInfo.startDate, periodInfo.endDate)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Mode:</span>
            <span className="stat-value">
              {periodInfo.mode === 'last-4-weeks' ? (
                <span style={{ color: '#3b82f6' }}>📅 Last 4 Full Weeks</span>
              ) : (
                <span style={{ color: '#ef4444' }}>🔴 Current Board</span>
              )}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg Effectiveness:</span>
            <span className="stat-value">
              {teamAIData.length > 0 
                ? (teamAIData.reduce((sum, member) => sum + member.metrics.aiEffectivenessScore, 0) / teamAIData.length).toFixed(1)
                : '0'
              }
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Status:</span>
            <span className="stat-value">
              {periodInfo.currentWeekIncluded ? (
                <span className="live-indicator">
                  <span className="live-badge">LIVE</span>
                </span>
              ) : (
                <span style={{ color: '#6b7280' }}>Complete Period</span>
              )}
            </span>
          </div>
        </div>
      </div>
      
      {/* Metric controls */}
      <div className="metric-controls">
        <div className="metric-selector">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => handleSort(e.target.value)}
            className="metric-select"
          >
            <option value="aiEffectivenessScore">AI Effectiveness Score</option>
            <option value="cursorEfficiency">Cursor Efficiency</option>
            <option value="aiToolEffectiveness">AI Tool Effectiveness</option>
            <option value="overallAIAdoption">Overall AI Adoption</option>
            <option value="totalAITime">Total AI Time</option>
            <option value="uniqueAIToolsCount">AI Tools Diversity</option>
          </select>
        </div>
      </div>
      
      {/* Table wrapper matching other screens */}
      <div className="table-wrapper">
        <table className="ai-overview-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th 
                className={`sortable ${sortBy === 'name' ? 'sorted' : ''}`}
                onClick={() => handleSort('name')}
              >
                Team Member {getSortIcon('name')}
              </th>
              <th 
                className={`sortable ${sortBy === 'aiEffectivenessScore' ? 'sorted' : ''}`}
                onClick={() => handleSort('aiEffectivenessScore')}
              >
                AI Effectiveness {getSortIcon('aiEffectivenessScore')}
              </th>
              <th 
                className={`sortable ${sortBy === 'cursorEfficiency' ? 'sorted' : ''}`}
                onClick={() => handleSort('cursorEfficiency')}
              >
                Cursor Efficiency {getSortIcon('cursorEfficiency')}
              </th>
              <th 
                className={`sortable ${sortBy === 'aiToolEffectiveness' ? 'sorted' : ''}`}
                onClick={() => handleSort('aiToolEffectiveness')}
              >
                AI Tool Usage {getSortIcon('aiToolEffectiveness')}
              </th>
              <th 
                className={`sortable ${sortBy === 'overallAIAdoption' ? 'sorted' : ''}`}
                onClick={() => handleSort('overallAIAdoption')}
              >
                AI Overall Adoption {getSortIcon('overallAIAdoption')}
              </th>
              <th 
                className={`sortable ${sortBy === 'uniqueAIToolsCount' ? 'sorted' : ''}`}
                onClick={() => handleSort('uniqueAIToolsCount')}
              >
                AI Tools {getSortIcon('uniqueAIToolsCount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {teamAIData.map((member, index) => (
              <tr key={member.userId} className={`user-row ${index < 3 ? `rank-${index + 1}` : ''}`}>
                <td className="rank-cell">
                  <span className="rank-number">{index + 1}</span>
                  {index === 0 && <span className="rank-badge gold">🥇</span>}
                  {index === 1 && <span className="rank-badge silver">🥈</span>}
                  {index === 2 && <span className="rank-badge bronze">🥉</span>}
                </td>
                <td className="team-member-cell">
                  <strong>{member.name}</strong>
                  <br />
                  <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>{member.email}</small>
                </td>
                <td className="effectiveness-score-cell">
                  {renderEffectivenessScore(member.metrics.aiEffectivenessScore)}
                </td>
                <td className="metric-cell">
                  <div>{member.metrics.cursorEfficiency.toFixed(1)}%</div>
                  <small style={{ color: '#7f8c8d' }}>
                    Assistance: {member.metrics.aiAssistanceRatio.toFixed(1)}%<br />
                    Productivity: {member.metrics.productivityScore.toFixed(0)}
                  </small>
                </td>
                <td className="metric-cell">
                  <div>{member.metrics.aiToolEffectiveness.toFixed(1)}%</div>
                  <small style={{ color: '#7f8c8d' }}>
                    Adoption: {member.metrics.aiAdoptionScore.toFixed(1)}%
                  </small>
                </td>
                <td className="metric-cell">
                  <div>{member.metrics.overallAIAdoption.toFixed(1)}%</div>
                  <small style={{ color: '#7f8c8d' }}>
                    Cursor: {TeamAIDataService.formatTime(member.metrics.cursorTimeHours * 60)}<br />
                    AI Tools: {TeamAIDataService.formatTime(member.metrics.aiToolTimeHours * 60)}<br />
                    <strong>Total Work: {TeamAIDataService.formatTime(member.metrics.totalWorkTime)}</strong>
                  </small>
                </td>
                <td className="app-count-cell">
                  {member.metrics.uniqueAIToolsCount} tools
                  <br />
                  <small style={{ color: '#7f8c8d' }}>
                    {member.metrics.primaryAITool}
                  </small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {teamAIData.length === 0 && (
        <div className="no-data-message">
          No team members found for the rolling 4-week period.
        </div>
      )}
      
      {/* Legend */}
      <div className="ai-overview-stats" style={{ marginTop: '1rem' }}>
        <div className="stats-section">
          <div className="stat-item">
            <span className="stat-label">AI Effectiveness Levels:</span>
            <span className="stat-value">
              <span className="legend-badge" style={{ backgroundColor: '#22c55e', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>90+ Expert</span>
              <span className="legend-badge" style={{ backgroundColor: '#84cc16', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>80+ Power User</span>
              <span className="legend-badge" style={{ backgroundColor: '#eab308', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>70+ Proficient</span>
              <span className="legend-badge" style={{ backgroundColor: '#f97316', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>60+ Developing</span>
              <span className="legend-badge" style={{ backgroundColor: '#ef4444', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px', marginRight: '0.5rem' }}>50+ Beginner</span>
              <span className="legend-badge" style={{ backgroundColor: '#6b7280', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '3px' }}>0+ Minimal</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAISection;
